import React, { useEffect, useState } from 'react';
import { Icon, Modal, Popup } from 'semantic-ui-react';
import styled from 'styled-components';

import Button from '../../../components/Button';
import { dangerNotification, successNotification, warningNotification } from '../../../services/notification';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import {
  getStandaloneChargeCommissionCollaboratorActivationStatus,
  retryCommissionCollaboratorBiometry,
  StandaloneChargeCommissionCollaboratorActivationStatusDTO
} from '../../../services/standalone-charges';

import { CommissioningCollaborator } from '../types';
import BiometryStatusCard from './BiometryStatusCard';
import RetryBiometryConfirmModal from './RetryBiometryConfirmModal';

type Props = {
  open: boolean;
  collaborator: CommissioningCollaborator | null;
  onClose: () => void;
  onEditRegistration?: (collaborator: CommissioningCollaborator) => void;
  onRetrySuccess?: () => void;
};

const ModalText = styled.p`
  color: #4f5661;
  font-size: 13px;
  line-height: 1.45;
  margin: 0 0 12px;
`;

const StatusText = styled.p`
  color: #4f5661;
  font-size: 13px;
  margin: 0 0 4px;
  text-align: center;
`;

const QrWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0 12px;
`;

const QrImage = styled.img`
  width: 140px;
  height: 140px;
  border: 1px solid #e2e2e2;
  background: #ffffff;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CopyButton = styled.button`
  border: 0;
  background: transparent;
  color: #8c95a3;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: ${props => props.theme.main.color};
  }
`;

const RegisterBiometryModal: React.FC<Props> = ({
  open,
  collaborator,
  onClose,
  onEditRegistration,
  onRetrySuccess
}) => {
  const [activationStatus, setActivationStatus] =
    useState<StandaloneChargeCommissionCollaboratorActivationStatusDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);

  const isRejected =
    collaborator?.status === 'BIOMETRY_REJECTED' ||
    Boolean(activationStatus?.biometryRejected);
  const isSupportRequired =
    collaborator?.status === 'SUPPORT_REQUIRED' ||
    Boolean(activationStatus?.supportRequired);
  const canRetry = Boolean(activationStatus?.canRetryBiometry);

  const kycToken = activationStatus?.kycToken || '';
  const publicKycPageUrl = kycToken ? toAbsoluteUrl(`/biometria/${kycToken}`) : '';
  const qrPayload = activationStatus?.kycQrPayload || '';
  const buildModalQrImageUrl = (value?: string): string => {
    const payload = String(value || '').trim();
    if (!payload) return '';
    if (payload.startsWith('data:image/')) return payload;
    if (payload.startsWith('http://') || payload.startsWith('https://')) return payload;
    return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(payload)}`;
  };
  const qrSource = buildModalQrImageUrl(qrPayload);

  const loadActivationStatus = async () => {
    if (!collaborator?.id) return;

    setLoading(true);
    try {
      const response = await getStandaloneChargeCommissionCollaboratorActivationStatus(
        collaborator.id
      );
      setActivationStatus(response);
    } catch (error) {
      dangerNotification(
        'Erro',
        'Não foi possível consultar o status de biometria na Pagar.me.',
        7000
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setActivationStatus(null);
      setRetryModalOpen(false);
      return;
    }

    loadActivationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collaborator?.id]);

  const copyBiometryLink = () => {
    if (!publicKycPageUrl) return;

    navigator.clipboard
      .writeText(publicKycPageUrl)
      .then(() => successNotification('Sucesso', 'Link da página de biometria copiado.'))
      .catch(() => warningNotification('Atenção', 'Não foi possível copiar o link.'));
  };

  const openBiometryLink = () => {
    if (!publicKycPageUrl) return;
    window.open(publicKycPageUrl, '_blank', 'noopener,noreferrer');
  };

  const editRegistration = () => {
    if (!collaborator || !onEditRegistration) return;
    onEditRegistration(collaborator);
  };

  const handleRetryBiometry = async () => {
    if (!collaborator?.id) return;
    setRetryLoading(true);
    try {
      const response = await retryCommissionCollaboratorBiometry(collaborator.id);
      setActivationStatus(response);
      setRetryModalOpen(false);
      successNotification(
        'Sucesso',
        'Novo cadastro criado. Conclua a biometria para liberar comissões.'
      );
      onRetrySuccess?.();
      await loadActivationStatus();
    } catch (error) {
      dangerNotification(
        'Erro',
        error instanceof Error
          ? error.message
          : 'Não foi possível iniciar uma nova tentativa de biometria.'
      );
    } finally {
      setRetryLoading(false);
    }
  };

  const modalTitle = isRejected
    ? 'Biometria recusada'
    : isSupportRequired
      ? 'Análise necessária'
      : 'Cadastrar biometria';

  return (
    <>
      <Modal open={open} onClose={onClose} size="tiny" closeIcon>
        <Modal.Header>
          <Icon name="tag" /> {modalTitle}
        </Modal.Header>
        <Modal.Content>
          {isSupportRequired && (
            <BiometryStatusCard variant="support" meta={activationStatus} compact />
          )}

          {isRejected && (
            <BiometryStatusCard
              variant={canRetry ? 'rejected' : 'limitReached'}
              meta={activationStatus}
              compact
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {canRetry && (
                  <Button
                    type="button"
                    size="small"
                    text="Tentar biometria novamente"
                    onClick={() => setRetryModalOpen(true)}
                    disabled={loading}
                  />
                )}
                <Button
                  type="button"
                  size="small"
                  text="Atualizar"
                  light
                  loading={loading}
                  disabled={loading}
                  onClick={loadActivationStatus}
                />
              </div>
            </BiometryStatusCard>
          )}

          {!isRejected && !isSupportRequired && (
            <>
              <h4 style={{ margin: '0 0 8px' }}>Biometria pendente na Pagar.me</h4>
              <ModalText>
                Seu cadastro foi enviado, mas o recebimento de comissões permanece bloqueado
                até a conclusão da prova de vida.
              </ModalText>

              <StatusText>
                Status da biometria:{' '}
                <strong>
                  {activationStatus?.status === 'ACTIVE' ? 'Ativa' : 'Pendente'}
                </strong>
              </StatusText>
              <StatusText>
                <Button
                  type="button"
                  size="small"
                  text="Atualizar"
                  light
                  loading={loading}
                  disabled={loading}
                  onClick={loadActivationStatus}
                />
              </StatusText>

              {qrSource && (
                <QrWrapper>
                  <QrImage src={qrSource} alt="QR Code para cadastro de biometria" />
                </QrWrapper>
              )}

              <ActionsRow>
                {onEditRegistration && (
                  <Button
                    type="button"
                    size="small"
                    text="Editar cadastro"
                    onClick={editRegistration}
                    disabled={loading}
                  />
                )}
                <Button
                  type="button"
                  size="small"
                  text="Acessar página de biometria"
                  onClick={openBiometryLink}
                  disabled={!publicKycPageUrl || loading}
                />
                <Popup
                  content="Copiar link da página"
                  trigger={
                    <CopyButton
                      type="button"
                      onClick={copyBiometryLink}
                      disabled={!publicKycPageUrl}
                    >
                      <Icon name="copy" />
                    </CopyButton>
                  }
                />
              </ActionsRow>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button type="button" text="Fechar" onClick={onClose} />
        </Modal.Actions>
      </Modal>

      <RetryBiometryConfirmModal
        open={retryModalOpen}
        loading={retryLoading}
        attemptCount={activationStatus?.recipientAttemptCount}
        maxAttempts={activationStatus?.maxRecipientAttempts}
        subjectLabel="colaborador"
        onConfirm={handleRetryBiometry}
        onClose={() => setRetryModalOpen(false)}
      />
    </>
  );
};

export default RegisterBiometryModal;
